import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MdCheckCircle } from 'react-icons/md';
import { validateEmail } from './validation';
import { t } from '../../utils/i18n';

/**
 * Magic link state machine:
 * idle -> sending -> sent | error
 */
const ML_STATE = { idle: 'idle', sending: 'sending', sent: 'sent' };

export default function MagicLinkForm({ onSendLink, setError }) {
  const [state, setState] = useState(ML_STATE.idle);
  const [email, setEmail] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const handleSendLink = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    setSubmitError('');
    setError('');

    const res = validateEmail(email);
    if (!res.valid) {
      setFieldErrors({ email: res.message });
      return;
    }

    setState(ML_STATE.sending);
    const result = await onSendLink(email);
    if (result.success) {
      setState(ML_STATE.sent);
    } else {
      setState(ML_STATE.idle);
      setSubmitError(result.error);
    }
  };

  const handleResend = async () => {
    setSubmitError('');
    const result = await onSendLink(email);
    if (!result.success) setSubmitError(result.error);
  };

  const handleChangeEmail = () => {
    setState(ML_STATE.idle);
    setFieldErrors({});
    setSubmitError('');
  };

  if (state === ML_STATE.sent) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl flex items-start gap-3">
          <MdCheckCircle className="text-green-600 dark:text-green-400 text-xl flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-1">
              Link je poslat na {email}
            </p>
            <p className="text-xs text-green-700 dark:text-green-300">
              Proverite Inbox i Spam folder. Link je važeći 15 minuta.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 justify-center text-sm">
          <button
            type="button"
            onClick={handleResend}
            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
          >
            Pošalji ponovo
          </button>
          <button
            type="button"
            onClick={handleChangeEmail}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            Promeni email
          </button>
        </div>

        <p className="text-sm text-center text-gray-600 dark:text-gray-400">
          Nemate nalog?{' '}
          <Link
            to="/register"
            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold"
          >
            Registrujte se
          </Link>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSendLink} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1.5 text-gray-900 dark:text-gray-100">Email adresa</label>
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setFieldErrors((prev) => ({ ...prev, email: null }));
          }}
          required
          autoComplete="email"
          className={`input-base ${fieldErrors.email ? 'input-error' : ''}`}
          placeholder={t('auth.login.emailPlaceholder') || 'vas@email.com'}
        />
        {fieldErrors.email && <p className="error-text">{fieldErrors.email}</p>}
      </div>

      {submitError && (
        <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {submitError}
        </div>
      )}

      <button
        type="submit"
        disabled={state === ML_STATE.sending || !email}
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {state === ML_STATE.sending ? (
          <>
            <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            Šalje se...
          </>
        ) : (
          'Pošalji link'
        )}
      </button>

      <p className="text-sm text-center text-gray-600 dark:text-gray-400">
        Nemate nalog?{' '}
        <Link
          to="/register"
          className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold"
        >
          Registrujte se
        </Link>
      </p>
    </form>
  );
}
